import React, { useState, useEffect } from 'react';
import { get, getDatabase, ref as dbRef, onValue } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AddCredits.css';

const poolOf = (entry) => {
    if (entry && typeof entry === 'object') {
        const monthly = Number(entry.monthly) || 0;
        const bonus = Number(entry.bonus) || 0;
        return { monthly, bonus, total: Math.round((monthly + bonus) * 100) / 100 };
    }
    const monthly = Number(entry) || 0;
    return { monthly, bonus: 0, total: monthly };
};

function AddCredits() {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [userCredits, setUserCredits] = useState({});
    const [creditAmount, setCreditAmount] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    
    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Load all users
    useEffect(() => {
        if (!currentUser) return;

        setIsLoading(true);
        const usersRef = dbRef(db, 'Users');
        const unsubscribe = onValue(usersRef, async (snapshot) => {
            const usersData = snapshot.val();
            if (!usersData) {
                setUsers([]);
                setIsLoading(false);
                return;
            }

            // Convert to array and load credits for each
            const usersArray = Object.entries(usersData).map(([id, data]) => ({
                id,
                email: data.email || 'No email',
                name: data.name || data.user_name || '',
                companyName: data.companyName || data.company_name || '',
                isPremium: data.isPremium || false,
                createdAt: data.createdAt || ''
            }));

            // Sort by email
            usersArray.sort((a, b) => a.email.localeCompare(b.email));
            
            setUsers(usersArray);
            setFilteredUsers(usersArray);
            
            // Load credits for all users
            await loadAllCredits(usersArray);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, currentUser]);

    // Load credits for all users
    const loadAllCredits = async (usersList) => {
        const creditsMap = {};
        for (const user of usersList) {
            try {
                const creditsRef = dbRef(db, `World/${user.id}/credits`);
                const snapshot = await get(creditsRef);
                if (snapshot.exists()) {
                    const node = snapshot.val() || {};
                    creditsMap[user.id] = {
                        monthly: Number(node.spyCredits) || 0,
                        bonus: Number(node.bonusCredits) || 0,
                    };
                } else {
                    creditsMap[user.id] = { monthly: 0, bonus: 0 };
                }
            } catch (err) {
                creditsMap[user.id] = 0;
            }
        }
        setUserCredits(creditsMap);
    };

    // Filter users based on search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredUsers(users);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = users.filter(user => 
                user.email.toLowerCase().includes(query) ||
                user.name.toLowerCase().includes(query) ||
                user.companyName.toLowerCase().includes(query)
            );
            setFilteredUsers(filtered);
        }
    }, [searchQuery, users]);

    // Add credits to selected user via admin Cloud Function
    const handleAddCredits = async () => {
        if (!selectedUser || !creditAmount) {
            setErrorMessage('Please select a user and enter a credit amount.');
            return;
        }

        const amount = parseFloat(creditAmount);
        if (isNaN(amount) || amount <= 0) {
            setErrorMessage('Please enter a valid credit amount.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const fnInstance = getFunctions(undefined, 'europe-west1');
            const addCreditsFn = httpsCallable(fnInstance, 'addCreditsAdmin');
            const result = await addCreditsFn({
                targetUserId: selectedUser.id,
                amount,
            });
            
            setUserCredits(prev => ({
                ...prev,
                [selectedUser.id]: {
                    monthly: result.data.monthlyBalance ?? poolOf(prev[selectedUser.id]).monthly,
                    bonus: result.data.bonusBalance ?? ((poolOf(prev[selectedUser.id]).bonus) + amount),
                },
            }));
            
            setSuccessMessage(`Added ${amount} extra credits to ${selectedUser.email}. They stay after the monthly reset. Spendable now: ${result.data.newBalance}`);
            setCreditAmount('');
            setSelectedUser(null);
        } catch (error) {
            console.error('Error adding credits:', error);
            const msg = error?.message || '';
            if (msg.includes('Admin access required') || msg.includes('permission-denied')) {
                setErrorMessage('Admin access required. Please make sure your account has admin privileges.');
            } else {
                setErrorMessage(`Failed to add credits: ${msg}`);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Quick amount buttons
    const quickAmounts = [50, 100, 250, 500, 1000, 10000];

    if (isLoading) {
        return (
            <div className="add-credits-container">
                <div className="add-credits-loading">
                    <i className="fa fa-spinner fa-spin"></i>
                    <span>Loading users...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="add-credits-container">
            {/* Header */}
            <div className="add-credits-header">
                <div className="header-title-section">
                    <h2>Spy Credits Manager</h2>
                    <p>Extra credits stay after the monthly reset. The membership allowance still resets on the account's cycle.</p>
                </div>
                <div className="header-stats">
                    <div className="stat-box">
                        <span className="stat-value">{users.length}</span>
                        <span className="stat-label">Total Users</span>
                    </div>
                </div>
            </div>

            {/* Messages */}
            {errorMessage && (
                <div className="credits-message credits-error">
                    <i className="fa fa-exclamation-circle"></i>
                    <span>{errorMessage}</span>
                    <button onClick={() => setErrorMessage('')}>×</button>
                </div>
            )}
            {successMessage && (
                <div className="credits-message credits-success">
                    <i className="fa fa-check-circle"></i>
                    <span>{successMessage}</span>
                    <button onClick={() => setSuccessMessage('')}>×</button>
                </div>
            )}

            <div className="add-credits-content">
                {/* User Selection Panel */}
                <div className="users-panel">
                    <div className="panel-header">
                        <h3>Select User</h3>
                        <div className="search-box">
                            <i className="fa fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search by email, name or company..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button className="clear-search" onClick={() => setSearchQuery('')}>
                                    <i className="fa fa-times"></i>
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="users-list">
                        {filteredUsers.length === 0 ? (
                            <div className="no-users">
                                <i className="fa fa-user-slash"></i>
                                <span>No users found</span>
                            </div>
                        ) : (
                            filteredUsers.map(user => (
                                <div 
                                    key={user.id}
                                    className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedUser(user)}
                                >
                                    <div className="user-avatar">
                                        {user.email.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="user-info">
                                        <span className="user-email">{user.email}</span>
                                        {user.companyName && (
                                            <span className="user-company">{user.companyName}</span>
                                        )}
                                    </div>
                                    <div className="user-credits-badge">
                                        <span className="credits-icon">🪙</span>
                                        <span className="credits-value">{poolOf(userCredits[user.id]).total}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Credits Panel */}
                <div className="credits-panel">
                    <div className="panel-header">
                        <h3>Add Credits</h3>
                    </div>
                    
                    {selectedUser ? (
                        <div className="credits-form">
                            {/* Selected User Info */}
                            <div className="selected-user-card">
                                <div className="selected-user-avatar">
                                    {selectedUser.email.charAt(0).toUpperCase()}
                                </div>
                                <div className="selected-user-details">
                                    <span className="selected-email">{selectedUser.email}</span>
                                    {selectedUser.companyName && (
                                        <span className="selected-company">{selectedUser.companyName}</span>
                                    )}
                                    <div className="current-balance">
                                        Monthly: <strong>{poolOf(userCredits[selectedUser.id]).monthly}</strong> (resets)
                                    </div>
                                    <div className="current-balance">
                                        Extra: <strong>{poolOf(userCredits[selectedUser.id]).bonus}</strong> (kept)
                                    </div>
                                </div>
                            </div>

                            {/* Amount Input */}
                            <div className="amount-section">
                                <label>Extra credits to add</label>
                                <p className="current-balance">These stay available after the monthly allowance resets.</p>
                                <input
                                    type="number"
                                    className="amount-input"
                                    value={creditAmount}
                                    onChange={(e) => setCreditAmount(e.target.value)}
                                    placeholder="Enter amount"
                                    min="1"
                                />
                            </div>

                            {/* Quick Amounts */}
                            <div className="quick-amounts">
                                <span className="quick-label">Quick Add:</span>
                                <div className="quick-buttons">
                                    {quickAmounts.map(amount => (
                                        <button
                                            key={amount}
                                            className="quick-btn"
                                            onClick={() => setCreditAmount(amount.toString())}
                                        >
                                            +{amount}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Preview */}
                            {creditAmount && parseFloat(creditAmount) > 0 && (
                                <div className="credits-preview">
                                    <div className="preview-row">
                                        <span>Spendable now</span>
                                        <span>{poolOf(userCredits[selectedUser.id]).total}</span>
                                    </div>
                                    <div className="preview-row add">
                                        <span>Adding</span>
                                        <span>+{parseFloat(creditAmount)}</span>
                                    </div>
                                    <div className="preview-row total">
                                        <span>New Balance</span>
                                        <span>{poolOf(userCredits[selectedUser.id]).total + parseFloat(creditAmount)}</span>
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                className="submit-btn"
                                onClick={handleAddCredits}
                                disabled={isSubmitting || !creditAmount || parseFloat(creditAmount) <= 0}
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin"></i>
                                        Adding Credits...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-plus"></i>
                                        Add {creditAmount || '0'} Credits
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="no-selection">
                            <div className="no-selection-icon">👈</div>
                            <h4>Select a User</h4>
                            <p>Choose a user from the list to add credits to their account</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AddCredits;
